"use client";

import { useEffect, useState } from "react";

import { useRouter } from "next/navigation";

import { ChevronLeft, ChevronRight } from "lucide-react";

import { useHorizontalScroll } from "@/hooks/useHorizontalScroll";
import { BrandSearchError, brandSearchErrorMessage } from "@/lib/brand-search-state";
import { fetchSimilarBrandsViaBridge } from "@/lib/brand-private-bridge-client";
import { buildCanonicalBrandPath } from "@/lib/brand-routing";
import { cn } from "@/lib/cn";
import { SECTION_PADDING_X } from "@/styles/responsive-spacing";

interface SuggestedBrand {
  name: string;
  brandSlug?: string;
  subheading?: string;
  description?: string;
}

interface ExploreMoreSectionProps {
  currentBrandName: string;
}

// Brand Card Component
function BrandCard({
  brand,
  onExplore,
}: {
  brand: SuggestedBrand;
  onExplore: (name: string, brandSlug?: string) => void;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const description = brand.description || brand.subheading;

  // Truncate description for preview
  const truncatedDescription = description
    ? description.length > 150
      ? description.slice(0, 150) + "..."
      : description
    : "";

  return (
    <div
      className="flex w-[85vw] flex-shrink-0 snap-center flex-col overflow-hidden rounded-3xl bg-white md:w-[400px]"
      data-testid="brand-similar-card"
    >
      {/* Header */}
      <div className="flex items-center justify-between bg-surface-muted px-6 py-4">
        <h3 className="text-body-lg font-medium text-ink">{brand.name}</h3>
        <button
          onClick={() => onExplore(brand.name, brand.brandSlug)}
          data-testid="brand-similar-view-button"
          className="rounded-full bg-ink px-4 py-2 text-[11px] font-medium text-white transition-colors hover:bg-black"
        >
          View Brand
        </button>
      </div>

      {/* Content */}
      <div className="flex flex-1 flex-col justify-between p-6">
        <div className="space-y-4">
          <p className="text-body leading-relaxed text-ink-secondary">
            {isExpanded ? description : truncatedDescription}
          </p>

          {description && description.length > 150 && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-body text-ink underline decoration-ink-faint underline-offset-4 transition-colors hover:decoration-ink"
            >
              {isExpanded ? "Show less" : "Expand"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ExploreMoreSection({ currentBrandName }: ExploreMoreSectionProps) {
  const [suggestedBrands, setSuggestedBrands] = useState<SuggestedBrand[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resultBrand, setResultBrand] = useState("");
  const router = useRouter();

  // Horizontal scroll navigation
  const {
    scrollRef: scrollContainerRef,
    canScrollLeft,
    canScrollRight,
    scrollLeft,
    scrollRight,
    scrollToStart,
  } = useHorizontalScroll({
    threshold: 20, // Account for padding
    scrollAmount: 420, // Card width + gap
    deps: [suggestedBrands.length], // Recalculate when brands load
  });

  // Reset scroll position when brand changes
  useEffect(() => {
    scrollToStart();
  }, [currentBrandName, scrollToStart]);

  useEffect(() => {
    let cancelled = false;
    setSuggestedBrands([]);
    setResultBrand(currentBrandName);
    setError(null);
    const fetchSimilarBrands = async () => {
      try {
        setLoading(true);

        const response = await fetchSimilarBrandsViaBridge(
          currentBrandName,
          5 // Limit to 5 results for horizontal scroll
        );

        if (cancelled) return;
        if (response.length > 0) {
          const similarBrands: SuggestedBrand[] = response
            .filter(
              (brand) =>
                brand.brand_name &&
                brand.brand_name.trim().toLowerCase() !== currentBrandName.trim().toLowerCase()
            )
            .map((brand) => ({
              name: brand.brand_name || "",
              brandSlug: brand.brand_slug || undefined,
              subheading: brand.directory_subheading || brand.brand_description || undefined,
              description: brand.brand_description || undefined,
            }));

          setSuggestedBrands(similarBrands);
        } else {
          setSuggestedBrands([]);
        }
      } catch (error) {
        console.error("Error fetching similar brands:", error);
        if (!cancelled) {
          setSuggestedBrands([]);
          // The page's directory brand can lack a public search source/embedding;
          // the visitor cannot fix that by changing their search input here.
          setError(
            error instanceof BrandSearchError && (error.status === 400 || error.status === 404)
              ? "Similar brands are not available for this brand yet."
              : brandSearchErrorMessage(error)
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchSimilarBrands();
    return () => {
      cancelled = true;
    };
  }, [currentBrandName]);

  const handleExploreBrand = (brandName: string, brandSlug?: string) => {
    router.push(
      buildCanonicalBrandPath({
        brandName,
        brandSlug,
      })
    );
  };

  if (loading || resultBrand !== currentBrandName) {
    return (
      <section className="mb-16 md:mb-20">
        <div className="mb-8 flex items-center justify-between">
          <div className="space-y-2">
            <div className="h-6 w-48 animate-pulse rounded bg-gray-200" />
            <div className="h-4 w-72 animate-pulse rounded bg-gray-100" />
          </div>
        </div>
        <div className="flex gap-6 overflow-hidden">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-64 w-[85vw] flex-shrink-0 animate-pulse rounded-3xl bg-gray-100 md:w-[400px]"
            />
          ))}
        </div>
      </section>
    );
  }

  if (error || suggestedBrands.length === 0) {
    return (
      <section className="mb-16 md:mb-20" aria-label="Similar brands">
        <h2 className="mb-4 text-title text-ink">You Might Also Like</h2>
        <p role={error ? "alert" : "status"} className="text-body text-ink-muted">
          {error || "No similar brands found."}
        </p>
      </section>
    );
  }

  return (
    <section className="mb-16 md:mb-20" data-testid="brand-you-might-also-like-section">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div className="space-y-1">
          <h2 className="text-title text-ink">You Might Also Like</h2>
          <p className="text-body text-ink-muted">
            People who like {currentBrandName} are also buying items from these brands.
          </p>
        </div>

        {/* Navigation Arrows */}
        {suggestedBrands.length > 2 && (
          <div className="flex gap-2">
            <button
              onClick={scrollLeft}
              disabled={!canScrollLeft}
              className={cn(
                "flex h-10 w-10 items-center justify-center rounded-full bg-white transition-all",
                canScrollLeft
                  ? "text-ink hover:bg-surface-muted"
                  : "cursor-not-allowed text-ink-faint"
              )}
              aria-label="Scroll left"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              onClick={scrollRight}
              disabled={!canScrollRight}
              className={cn(
                "flex h-10 w-10 items-center justify-center rounded-full bg-white transition-all",
                canScrollRight
                  ? "text-ink hover:bg-surface-muted"
                  : "cursor-not-allowed text-ink-faint"
              )}
              aria-label="Scroll right"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        )}
      </div>

      {/* Horizontal Scrolling Brand Cards */}
      <div
        ref={scrollContainerRef}
        className="flex snap-x snap-mandatory gap-4 overflow-x-auto pb-4 scrollbar-hide md:gap-6"
        style={{
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          // Negative margins + matching padding allow edge-to-edge scrolling
          marginLeft: `calc(-1 * ${SECTION_PADDING_X.RESPONSIVE})`,
          marginRight: `calc(-1 * ${SECTION_PADDING_X.RESPONSIVE})`,
          paddingLeft: SECTION_PADDING_X.RESPONSIVE,
          paddingRight: SECTION_PADDING_X.RESPONSIVE,
          // Ensure snap points align with content padding
          scrollPaddingLeft: SECTION_PADDING_X.RESPONSIVE,
          scrollPaddingRight: SECTION_PADDING_X.RESPONSIVE,
        }}
      >
        {suggestedBrands.map((brand, index) => (
          <BrandCard key={`${brand.name}-${index}`} brand={brand} onExplore={handleExploreBrand} />
        ))}
      </div>
    </section>
  );
}
